import { FC, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRecoilState } from 'recoil';
import { Forward } from 'lucide-react';

import userStatusColor from '../../../../utils/userStatusColor';
import { EndUserActivity, GetUserProfile, GetUserStatus, StartUserActivity } from '../../../../services/User';
import { OnCallContainer } from './style';
import { IUserStatus } from '../../../../types/user';
import intakeIdState from '../../../../stores/atoms/intakeIdAtom';
import query from '../../../../constants/queries';
import sidebarState from '../../../../stores/atoms/sidebarAtom';
import Alert from '../../../../components/Alert';
import { useBoard } from '../../../../pages/CaseBoard/useBoard';
import WorkingSwitch from './WorkingSwitch';
import { statusName } from './helper';
import StatusSelector from './StatusSelector';
import AnsweringCallsSwitch from './AnsweringCallsSwitch';

const OnCall: FC = () => {
    const [working, setWorking] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [currentStatus, setCurrentStatus] = useState('');
    const [loadingStatus, setLoadingStatus] = useState(false);
    const [verifyIntake, setVerifyIntake] = useState(false);
    const [sidebarOpen, handleStatusBar] = useRecoilState(sidebarState);
    const [showOptions, setShowOptions] = useState(false);
    const [intakeId, setIntakeId] = useRecoilState(intakeIdState);
    const queryClient = useQueryClient();
    const { setIsReadOnly, setIsAnsweringCalls } = useBoard();
    const endActivityStatus = useMutation(EndUserActivity);
    const startActivityStatus = useMutation(StartUserActivity);
    const { data: user } = useQuery([query.userProfile], () => GetUserProfile());
    const { data: userStatus, isLoading } = useQuery([query.userStatuses], () => GetUserStatus(), {
        staleTime: currentStatus !== statusName.available ? 3 * 60000 : 1000 * 60 * 0.5,
        enabled: true,
        onSuccess: (data) => {
            if (data.length > 0) {
                setIsReadOnly(false);
                setApiStatus(data);
            } else {
                setIsReadOnly(true);
            }
        }
    });

    useEffect(() => {
        const interval = setInterval(() => {
            if (verifyIntake && currentStatus === statusName.available) {
                queryClient.invalidateQueries([query.userStatuses]);
            } else {
                clearInterval(interval);
            }
        }, 30000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [verifyIntake]);

    const reusedProps = {
        working,
        currentStatus,
        loadingStatus,
        isLoading,
        setLoadingStatus,
        endActivityStatus,
        setShowOptions
    };
    const statusSelectorProps = {
        ...reusedProps,
        startActivityStatus,
        showOptions
    };
    const workingSwitchProps = {
        ...reusedProps,
        setWorking,
        userStatus,
        setCurrentStatus
    };
    const { can_use_twilio, is_available_for_inbound_calls: isUserAnswering } = user || {};
    const showAnsweringCallSwitch = can_use_twilio && isUserAnswering !== null && isUserAnswering !== undefined;

    const setApiStatus = (statuses: IUserStatus[]) => {
        let intake = false;
        setShowOptions(false);

        for (let index = 0, total = statuses.length; index < total; index++) {
            const item = statuses[index];

            switch (item.type) {
                case statusName.working:
                    setWorking(true);
                    break;

                case statusName.online:
                    break;

                case statusName.available:
                    setCurrentStatus(item.type);
                    setVerifyIntake(true);
                    break;

                case statusName.pause:
                    setCurrentStatus(item.type);
                    setIsAnsweringCalls(false);
                    setTimeout(async () => {
                        setLoadingStatus(true);
                        await endActivityStatus.mutateAsync(statusName.pause);
                        await startActivityStatus.mutateAsync(statusName.available).then(() => {
                            queryClient.invalidateQueries([query.userStatuses]);
                        });
                        setLoadingStatus(false);
                    }, 10000);
                    break;

                case statusName.intake:
                    setIsAnsweringCalls(false);
                    if (item.case_id) setIntakeId(item?.case_id);
                    if (currentStatus === statusName.available) {
                        setShowAlert(true);
                    }
                    setCurrentStatus(item.type);
                    setWorking(true);
                    setVerifyIntake(false);
                    intake = true;
                    break;

                case statusName.break:
                    setCurrentStatus(item.type);
                    setIsAnsweringCalls(false);
                    break;

                default:
                    setCurrentStatus(item.type);
                    break;
            }
            if (intake) break;
        }
    };

    return (
        <>
            {showAlert && (
                <Alert icon={<Forward className="h-6 w-6" />} onClose={() => setShowAlert(false)}>
                    Case <a href={`/case-board/${intakeId}/notes`}>{intakeId}</a> was assigned to you
                </Alert>
            )}
            {sidebarOpen ? (
                <OnCallContainer>
                    <div className="border rounded-lg">
                        <WorkingSwitch {...workingSwitchProps} />
                        {working && <StatusSelector {...statusSelectorProps} />}
                        {showAnsweringCallSwitch && <AnsweringCallsSwitch isUserAnswering={isUserAnswering} />}
                    </div>
                </OnCallContainer>
            ) : (
                <div
                    className="flex justify-center rounded-sm capitalize text-xxxs text-white font-bold cursor-pointer p-2px mt-2 mx-1"
                    style={{ backgroundColor: userStatusColor(currentStatus) }}
                    onClick={() => handleStatusBar(true)}
                >
                    <span>
                        {currentStatus === statusName.intake
                            ? 'Intake'
                            : `${currentStatus.replace('_', ' ') ? currentStatus.replace('_', ' ') : 'Not working'}`}
                    </span>
                </div>
            )}
        </>
    );
};
export default OnCall;
